// 마케팅·이벤트 공유 피드 — distributor-desk Design §2.4, FR-10/FR-11.
// marketing_posts 를 status/post_type 뱃지와 함께 피드로 표시하고, 포스트를
// 펼치면 marketing_post_targets(국가별 배포 대상)를 수동으로 관리한다.
//
// 원칙: 자동 발송/자동 전이 없음 — 게시/보관/배포상태 표기는 전부 사용자 클릭.
// 참조 리스트(countries/partners)는 useDistributorData(). 이 페이지가 변경하는
// marketing_posts / assets 는 자체 state 로 로드하고 mutate 성공 후 재로드한다.
import { useCallback, useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured } from "@/lib/supabase";
import {
  marketingPostsRepo,
  distributorAssetsRepo,
  type NewMarketingPost,
  type NewMarketingPostTarget,
} from "@/lib/db";
import type {
  MarketingPost,
  MarketingPostTarget,
  MarketingPostType,
  MarketingPostStatus,
  MarketingDeliveryStatus,
  DistributorAsset,
} from "@/types";
import {
  Badge,
  Btn,
  Card,
  cx,
  Disclaimer,
  EmptyState,
  Field,
  IconChevron,
  IconSpark,
  Input,
  PageHeader,
  SectionLabel,
  Select,
  tdCls,
  Textarea,
  thCls,
  type Tone,
} from "./ui";
import { useDistributorData } from "./DistributorDeskLayout";

// ── labels & tones ──────────────────────────────────────────────────────────
const TYPE_LABEL: Record<MarketingPostType, string> = {
  marketing_asset: "마케팅 자료",
  promotion: "프로모션",
  event: "이벤트",
  announcement: "공지",
};
const TYPE_TONE: Record<MarketingPostType, Tone> = {
  marketing_asset: "neutral",
  promotion: "accent",
  event: "accent",
  announcement: "accent",
};
const STATUS_LABEL: Record<MarketingPostStatus, string> = {
  draft: "초안",
  published: "게시",
  archived: "보관",
};
const STATUS_TONE: Record<MarketingPostStatus, Tone> = {
  draft: "neutral",
  published: "success",
  archived: "neutral",
};
const DELIVERY_LABEL: Record<MarketingDeliveryStatus, string> = {
  pending: "대기",
  sent: "발송표기",
  viewed: "열람표기",
};
const DELIVERY_TONE: Record<MarketingDeliveryStatus, Tone> = {
  pending: "neutral",
  sent: "info",
  viewed: "success",
};
const DELIVERY_ORDER: MarketingDeliveryStatus[] = ["pending", "sent", "viewed"];
const TYPE_OPTIONS: MarketingPostType[] = [
  "announcement",
  "marketing_asset",
  "promotion",
  "event",
];

const today = (): string => new Date().toISOString().slice(0, 10);
const dayOnly = (s: string | null | undefined): string => (s ? s.slice(0, 10) : "—");

// ── shared draft shape for create/edit forms ────────────────────────────────
interface PostDraft {
  title: string;
  post_type: MarketingPostType;
  body_md: string;
  asset_id: string; // "" = none
  event_start: string;
  event_end: string;
}
const EMPTY_DRAFT: PostDraft = {
  title: "",
  post_type: "announcement",
  body_md: "",
  asset_id: "",
  event_start: "",
  event_end: "",
};
const draftFromPost = (p: MarketingPost): PostDraft => ({
  title: p.title,
  post_type: p.post_type,
  body_md: p.body_md,
  asset_id: p.asset_id ?? "",
  event_start: p.event_start ?? "",
  event_end: p.event_end ?? "",
});
/** Editable field subset shared by create+update (status is set via transitions). */
function draftToFields(dr: PostDraft) {
  const isEvent = dr.post_type === "event";
  return {
    title: dr.title.trim(),
    post_type: dr.post_type,
    body_md: dr.body_md,
    asset_id: dr.asset_id || null,
    event_start: isEvent ? dr.event_start || null : null,
    event_end: isEvent ? dr.event_end || null : null,
  };
}

export function MarketingPage(): JSX.Element {
  const d = useDistributorData();
  const configured = isSupabaseConfigured();

  const [posts, setPosts] = useState<MarketingPost[]>([]);
  const [assets, setAssets] = useState<DistributorAsset[]>([]);
  const [loading, setLoading] = useState(true);

  // create panel
  const [showCreate, setShowCreate] = useState(false);
  const [createDraft, setCreateDraft] = useState<PostDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  // inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<PostDraft>(EMPTY_DRAFT);

  // targets (per expanded post)
  const [openPost, setOpenPost] = useState<string | null>(null);
  const [targets, setTargets] = useState<Record<string, MarketingPostTarget[]>>({});
  const [tCountry, setTCountry] = useState("");
  const [tPartner, setTPartner] = useState("");

  // status filter chips
  const [filter, setFilter] = useState<"all" | MarketingPostStatus>("all");

  const reload = useCallback(async () => {
    setLoading(true);
    const [p, a] = await Promise.all([
      marketingPostsRepo.list().catch(() => [] as MarketingPost[]),
      distributorAssetsRepo.list().catch(() => [] as DistributorAsset[]),
    ]);
    setPosts(p);
    setAssets(a);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!configured) {
      setLoading(false);
      return;
    }
    void reload();
  }, [configured, reload]);

  const loadTargets = useCallback(async (postId: string) => {
    try {
      const t = await marketingPostsRepo.targets(postId);
      setTargets((prev) => ({ ...prev, [postId]: t }));
    } catch {
      setTargets((prev) => ({ ...prev, [postId]: [] }));
    }
  }, []);

  // ── reference label helpers ───────────────────────────────────────────────
  const countryLabel = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return "전 국가";
      const c = d.countries.find((x) => x.id === id);
      return c ? c.name_ko ?? c.name_en : id;
    },
    [d.countries],
  );
  const partnerName = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return "전 총판";
      return d.partners.find((p) => p.id === id)?.company_name ?? "(미상 총판)";
    },
    [d.partners],
  );
  const assetTitle = useCallback(
    (id: string | null | undefined): string | null => {
      if (!id) return null;
      return assets.find((a) => a.id === id)?.title ?? "(삭제된 자산)";
    },
    [assets],
  );

  // ── feed ──────────────────────────────────────────────────────────────────
  const feed = useMemo(() => {
    const sorted = [...posts].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    return filter === "all" ? sorted : sorted.filter((p) => p.status === filter);
  }, [posts, filter]);

  const counts = useMemo(() => {
    const c = { all: posts.length, draft: 0, published: 0, archived: 0 };
    for (const p of posts) c[p.status] += 1;
    return c;
  }, [posts]);

  // ── mutations (all manual; reload after success) ──────────────────────────
  async function createPost(): Promise<void> {
    const fields = draftToFields(createDraft);
    if (!fields.title) return;
    setSaving(true);
    try {
      const row: NewMarketingPost = {
        ...fields,
        status: "draft",
        published_at: null,
        created_by: null,
      };
      await marketingPostsRepo.create(row);
      setCreateDraft(EMPTY_DRAFT);
      setShowCreate(false);
      await reload();
    } catch (e) {
      window.alert("포스트 생성 실패: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit(): Promise<void> {
    if (!editingId) return;
    const fields = draftToFields(editDraft);
    if (!fields.title) return;
    setSaving(true);
    try {
      await marketingPostsRepo.update(editingId, fields);
      setEditingId(null);
      await reload();
    } catch (e) {
      window.alert("수정 실패: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  async function transition(p: MarketingPost, next: MarketingPostStatus): Promise<void> {
    const patch =
      next === "published"
        ? { status: next, published_at: new Date().toISOString() }
        : next === "draft"
          ? { status: next, published_at: null }
          : { status: next };
    try {
      await marketingPostsRepo.update(p.id, patch);
      await reload();
    } catch (e) {
      window.alert("상태 변경 실패: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function removePost(p: MarketingPost): Promise<void> {
    if (!window.confirm(`포스트 "${p.title}" 을(를) 삭제할까요? 되돌릴 수 없습니다.`)) return;
    try {
      await marketingPostsRepo.remove(p.id);
      if (openPost === p.id) setOpenPost(null);
      await reload();
    } catch (e) {
      window.alert("삭제 실패: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function toggleExpand(postId: string): Promise<void> {
    if (openPost === postId) {
      setOpenPost(null);
      return;
    }
    setOpenPost(postId);
    setTCountry("");
    setTPartner("");
    if (!targets[postId]) await loadTargets(postId);
  }

  async function addTarget(postId: string): Promise<void> {
    if (!tCountry && !tPartner) return; // require at least one scope
    try {
      const row: NewMarketingPostTarget = {
        post_id: postId,
        country_id: tCountry || null,
        partner_id: tPartner || null,
        delivery_status: "pending",
        delivered_at: null,
      };
      await marketingPostsRepo.addTarget(row);
      setTCountry("");
      setTPartner("");
      await loadTargets(postId);
    } catch (e) {
      window.alert("배포 대상 추가 실패: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function setTargetStatus(
    postId: string,
    t: MarketingPostTarget,
    status: MarketingDeliveryStatus,
  ): Promise<void> {
    if (t.delivery_status === status) return;
    try {
      await marketingPostsRepo.updateTarget(t.id, {
        delivery_status: status,
        delivered_at: status === "pending" ? null : t.delivered_at ?? today(),
      });
      await loadTargets(postId);
    } catch (e) {
      window.alert("배포 상태 변경 실패: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function removeTarget(postId: string, t: MarketingPostTarget): Promise<void> {
    if (!window.confirm("이 배포 대상을 제거할까요?")) return;
    try {
      await marketingPostsRepo.removeTarget(t.id);
      await loadTargets(postId);
    } catch (e) {
      window.alert("배포 대상 제거 실패: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        eyebrow="Marketing"
        title="마케팅·이벤트 피드"
        subtitle="마케팅 자료·프로모션·이벤트·공지를 게시하고 국가별 배포 대상을 수동으로 관리합니다. 자동 발송·자동 전이 없음."
        actions={
          <Btn
            variant={showCreate ? "ghost" : "primary"}
            onClick={() => {
              setCreateDraft(EMPTY_DRAFT);
              setShowCreate((v) => !v);
            }}
            disabled={!configured}
          >
            {showCreate ? "닫기" : "새 포스트"}
          </Btn>
        }
      />

      <div className="space-y-5">
        {!configured && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-700">
            Supabase 미설정 — 읽기 전용 빈 화면입니다.{" "}
            <code className="rounded bg-amber-100 px-1 py-0.5 text-[12px]">.env</code> 설정 후 피드가 로드됩니다.
          </div>
        )}

        {/* create panel */}
        {showCreate && configured && (
          <Card className="p-4">
            <SectionLabel>새 포스트 작성</SectionLabel>
            <div className="mt-3">
              <PostFormFields draft={createDraft} onChange={setCreateDraft} assets={assets} />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Btn
                variant="primary"
                onClick={() => void createPost()}
                disabled={saving || !createDraft.title.trim()}
              >
                초안으로 저장
              </Btn>
              <Btn variant="ghost" onClick={() => setShowCreate(false)} disabled={saving}>
                취소
              </Btn>
              <span className="text-[11px] text-slate-400">
                저장 후 목록에서 [게시] 버튼을 눌러야 공개됩니다. 자동 발송 없음.
              </span>
            </div>
          </Card>
        )}

        {/* status filter chips */}
        <div className="flex flex-wrap gap-1.5">
          {(["all", "draft", "published", "archived"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className={cx(
                "rounded-full px-3 py-1 text-[12px] font-medium transition",
                filter === k
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "border border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
              )}
            >
              {k === "all" ? "전체" : STATUS_LABEL[k]} ({counts[k]})
            </button>
          ))}
        </div>

        {/* feed */}
        {loading ? (
          <Card className="px-4 py-16">
            <div className="text-center text-sm text-slate-400">불러오는 중…</div>
          </Card>
        ) : feed.length === 0 ? (
          <EmptyState icon={<IconSpark className="h-8 w-8" />} title="표시할 포스트가 없습니다">
            상단 [새 포스트] 로 마케팅 자료·프로모션·이벤트·공지를 등록하면 이 피드에 취합됩니다.
          </EmptyState>
        ) : (
          <div className="space-y-3">
            {feed.map((p) =>
              editingId === p.id ? (
                <Card key={p.id} className="p-4 ring-1 ring-indigo-200">
                  <SectionLabel>포스트 편집</SectionLabel>
                  <div className="mt-3">
                    <PostFormFields draft={editDraft} onChange={setEditDraft} assets={assets} />
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Btn
                      variant="primary"
                      onClick={() => void saveEdit()}
                      disabled={saving || !editDraft.title.trim()}
                    >
                      저장
                    </Btn>
                    <Btn variant="ghost" onClick={() => setEditingId(null)} disabled={saving}>
                      취소
                    </Btn>
                  </div>
                </Card>
              ) : (
                <Card key={p.id} as="section">
                  <div className="flex flex-wrap items-start justify-between gap-2 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={TYPE_TONE[p.post_type]}>{TYPE_LABEL[p.post_type]}</Badge>
                        <Badge tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                        <h3 className="m-0 text-[14px] font-semibold text-slate-800">{p.title}</h3>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                        {p.post_type === "event" && (p.event_start || p.event_end) && (
                          <span className="text-indigo-500">
                            기간 {dayOnly(p.event_start)} ~ {dayOnly(p.event_end)}
                          </span>
                        )}
                        {p.status === "published" && <span>게시일 {dayOnly(p.published_at)}</span>}
                        {assetTitle(p.asset_id) && <span>첨부 · {assetTitle(p.asset_id)}</span>}
                        <span>작성 {dayOnly(p.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-1.5">
                      {p.status === "draft" && (
                        <Btn variant="subtle" onClick={() => void transition(p, "published")}>
                          게시
                        </Btn>
                      )}
                      {p.status === "published" && (
                        <Btn variant="subtle" onClick={() => void transition(p, "archived")}>
                          보관
                        </Btn>
                      )}
                      {p.status === "archived" && (
                        <Btn variant="subtle" onClick={() => void transition(p, "draft")}>
                          초안 복원
                        </Btn>
                      )}
                      <Btn
                        variant="subtle"
                        onClick={() => {
                          setEditingId(p.id);
                          setEditDraft(draftFromPost(p));
                        }}
                      >
                        편집
                      </Btn>
                      <Btn variant="danger" onClick={() => void removePost(p)}>
                        삭제
                      </Btn>
                    </div>
                  </div>

                  {p.body_md.trim() && (
                    <pre className="mx-3 mb-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs leading-relaxed text-slate-700">
                      {p.body_md}
                    </pre>
                  )}

                  {/* targets */}
                  <div className="border-t border-slate-100 px-3 py-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-[12px] font-medium text-slate-500 transition hover:text-slate-800"
                      onClick={() => void toggleExpand(p.id)}
                    >
                      <IconChevron
                        className={cx("h-3.5 w-3.5 transition-transform", openPost === p.id && "rotate-90")}
                      />
                      국가별 배포 대상
                      {targets[p.id] ? ` (${targets[p.id]!.length})` : ""}
                    </button>

                    {openPost === p.id && (
                      <div className="mt-2 space-y-2">
                        {(targets[p.id]?.length ?? 0) === 0 ? (
                          <p className="text-[11px] text-slate-400">
                            등록된 배포 대상이 없습니다. 아래에서 국가·총판을 지정해 추가하세요.
                          </p>
                        ) : (
                          <div className="overflow-hidden rounded-lg border border-slate-200/70">
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr className="border-b border-slate-200/70">
                                    {["국가", "총판", "배포 상태", "표기일", ""].map((h) => (
                                      <th key={h} className={thCls}>
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {targets[p.id]!.map((t) => (
                                    <tr key={t.id} className="transition hover:bg-slate-50/60">
                                      <td className={cx(tdCls, "whitespace-nowrap")}>
                                        {countryLabel(t.country_id)}
                                      </td>
                                      <td className={cx(tdCls, "whitespace-nowrap")}>
                                        {partnerName(t.partner_id)}
                                      </td>
                                      <td className={tdCls}>
                                        <div className="flex items-center gap-2">
                                          <Badge tone={DELIVERY_TONE[t.delivery_status]}>
                                            {DELIVERY_LABEL[t.delivery_status]}
                                          </Badge>
                                          <span className="flex gap-0.5">
                                            {DELIVERY_ORDER.map((s) => (
                                              <button
                                                key={s}
                                                type="button"
                                                title={"상태를 " + DELIVERY_LABEL[s] + "(으)로 표기"}
                                                onClick={() => void setTargetStatus(p.id, t, s)}
                                                className={cx(
                                                  "rounded-md border px-1.5 py-0.5 text-[10px] transition",
                                                  t.delivery_status === s
                                                    ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                                                    : "border-slate-200 bg-white text-slate-400 hover:bg-slate-50",
                                                )}
                                              >
                                                {DELIVERY_LABEL[s]}
                                              </button>
                                            ))}
                                          </span>
                                        </div>
                                      </td>
                                      <td className={cx(tdCls, "whitespace-nowrap text-slate-500")}>
                                        {dayOnly(t.delivered_at)}
                                      </td>
                                      <td className={cx(tdCls, "text-right")}>
                                        <button
                                          type="button"
                                          className="border-0 bg-transparent p-0 text-[11px] text-rose-600 hover:underline"
                                          onClick={() => void removeTarget(p.id, t)}
                                        >
                                          제거
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* add target */}
                        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-3">
                          <Field label="국가" className="min-w-[150px]">
                            <Select value={tCountry} onChange={(e) => setTCountry(e.target.value)}>
                              <option value="">전 국가</option>
                              {d.countries.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name_ko ?? c.name_en}
                                </option>
                              ))}
                            </Select>
                          </Field>
                          <Field label="총판" className="min-w-[150px]">
                            <Select value={tPartner} onChange={(e) => setTPartner(e.target.value)}>
                              <option value="">전 총판</option>
                              {d.partners.map((pt) => (
                                <option key={pt.id} value={pt.id}>
                                  {pt.company_name}
                                </option>
                              ))}
                            </Select>
                          </Field>
                          <Btn
                            variant="ghost"
                            onClick={() => void addTarget(p.id)}
                            disabled={!tCountry && !tPartner}
                          >
                            대상 추가
                          </Btn>
                          <span className="pb-2 text-[11px] text-slate-400">
                            배포 상태는 담당자가 수동 표기합니다. 자동 발송 없음.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ),
            )}
          </div>
        )}

        <Disclaimer>
          본 도구는 사전 정의된 항목만 취합·표시합니다. 최종 판단·확인은 담당자 책임입니다.
        </Disclaimer>
      </div>
    </div>
  );
}

// ── shared form fields (create + edit) ──────────────────────────────────────
function PostFormFields({
  draft,
  onChange,
  assets,
}: {
  draft: PostDraft;
  onChange: (d: PostDraft) => void;
  assets: DistributorAsset[];
}): JSX.Element {
  const set = (patch: Partial<PostDraft>) => onChange({ ...draft, ...patch });

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="제목">
          <Input
            value={draft.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="포스트 제목"
          />
        </Field>
        <Field label="유형">
          <Select
            value={draft.post_type}
            onChange={(e) => set({ post_type: e.target.value as MarketingPostType })}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="첨부 자산 (선택)">
          <Select value={draft.asset_id} onChange={(e) => set({ asset_id: e.target.value })}>
            <option value="">첨부 없음</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </Select>
        </Field>
        {draft.post_type === "event" && (
          <div className="grid grid-cols-2 gap-2">
            <Field label="시작일">
              <Input
                type="date"
                value={draft.event_start}
                onChange={(e) => set({ event_start: e.target.value })}
              />
            </Field>
            <Field label="종료일">
              <Input
                type="date"
                value={draft.event_end}
                onChange={(e) => set({ event_end: e.target.value })}
              />
            </Field>
          </div>
        )}
      </div>

      <Field label="본문 (Markdown 원문 — 렌더링 없이 그대로 저장·표시)">
        <Textarea
          value={draft.body_md}
          onChange={(e) => set({ body_md: e.target.value })}
          rows={6}
          placeholder="## 제목&#10;- 항목1&#10;- 항목2"
          className="font-mono"
        />
      </Field>
    </div>
  );
}
